import React, { useState, useEffect } from 'react';
import { Typography, Button, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TextField, makeStyles } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import axios from 'axios';
import { connect } from 'redux-zero/react';
import actions from "../../actions";
import AccountEdit from './AccountEdit';
import ConfirmDialog from '../ConfirmDialog';

const useStyles = makeStyles(theme => ({
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10
    },
    search: {
        width: 300,
        maxWidth: '100%'
    },
    actionButton: {
        cursor: 'pointer'
    },
    disabledRow: {
        '& .MuiTableCell-root': {
            color: theme.palette.text.disabled,
            fontStyle: 'italic'
        }
    }
}));

// Member accounts are prefixed with the flat no
function accountName(row) {
    return (row.unit ? row.unit + ' - ' : '') + row.name;
}

// Matches code, flat no or name, ignoring case
function matchesSearch(row, search) {
    const text = search.trim().toLowerCase();
    return (row.code + ' ' + accountName(row)).toLowerCase().includes(text);
}

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

function Accounts({ selectedSociety }) {
    const classes = useStyles();

    const [refreshData, setRefreshData] = React.useState(1);

    const [editMode, setEditMode] = React.useState(null);
    const [editModel, setEditModel] = React.useState();
    const [accounts, setAccounts] = useState([]);
    const [search, setSearch] = useState('');

    const [deleteModel, setDeleteModel] = React.useState();
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

    useEffect(() => {
        axios.get('/account/headers', { headers: { 'X-SocietyId': selectedSociety } })
            .then(response => {
                setAccounts(response.data);
            });
    }, [selectedSociety, refreshData]);

    function create() {
        setEditMode('Create');
        setEditModel({ id: 0, groupCode: '', code: '', name: '', type: 'Asset', depreciationPercentage: 0, isBillItem: false, isSystem: false });
    }

    function edit(row) {
        setEditMode('Edit');
        let groupCode = parseInt((row.code + '').substr(0, 3));
        row.accountGroup = { code: groupCode, name: row.groupName };
        setEditModel(row);
    }

    function closeEditModal() {
        setEditModel(null);
        setRefreshData(refreshData + 1);
    }

    function ddelete(row) {
        setDeleteModel(row);
        setShowConfirmDelete(true);
    }

    function closeConfirmDialog() {
        setShowConfirmDelete(false);
    }

    function deleteAccount(row) {
        closeConfirmDialog();
        axios({
            method: 'DELETE',
            url: '/account/' + row.id,
            headers: { 'X-SocietyId': selectedSociety }
        })
            .then(response => {
                setRefreshData(refreshData + 1);
            })
            .catch(error => {
                alert(error.response.data.message);
            });
    }

    function downloadIndexPdf() {
        axios({
            method: 'GET',
            url: '/account/pdf/index',
            headers: { 'X-SocietyId': selectedSociety },
            responseType: 'arraybuffer'
        }).then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            window.open(window.URL.createObjectURL(blob));
        });
    }

    return (
        <>
            <Typography variant="h5">Accounts</Typography>
            <div className={classes.topBar}>
                <TextField className={classes.search}
                    size="small"
                    label="Search"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)} />
                <div>
                    {window.decodedJwt.role === 'Administrator' &&
                        <Button variant="contained" size="small" color="primary" onClick={create}>Create</Button>
                    }
                    <Button variant="contained" size="small" color="primary" style={{ marginLeft: 8 }} onClick={downloadIndexPdf}>Index PDF</Button>
                </div>
            </div>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Group Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Bill Item</TableCell>
                            <TableCell>System</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accounts.filter(x => matchesSearch(x, search)).map((row, index) => (
                            <TableRow hover key={index} className={row.disabled ? classes.disabledRow : undefined}>
                                <TableCell>{row.code}</TableCell>
                                <TableCell>{accountName(row)}{row.disabled ? ' (Disabled)' : ''}</TableCell>
                                <TableCell>{row.groupName}</TableCell>
                                <TableCell>{row.type}</TableCell>
                                <TableCell>{row.isBillItem ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{row.isSystem ? 'Yes' : 'No'}</TableCell>
                                <TableCell>
                                    {window.decodedJwt.role === 'Administrator' && row.isBillItem === false && row.type !== 'Member' && (
                                        <>
                                            <EditIcon className={classes.actionButton} onClick={() => edit(row)} />
                                            {row.isSystem === false && (
                                                <DeleteIcon className={classes.actionButton} onClick={() => ddelete(row)} />
                                            )}
                                        </>)
                                    }
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <AccountEdit mode={editMode} model={editModel} onClose={closeEditModal} />
            <ConfirmDialog model={deleteModel}
                show={showConfirmDelete}
                title="Confirm Delete"
                message="Are you sure you want to delete this account?"
                onClose={closeConfirmDialog}
                onYes={deleteAccount} />
        </>
    );
}

export default connect(mapToProps, actions)(Accounts);